import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, apiWrite, type Engagement } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDateTime } from '../lib/format'

// Counterparty network (part 155). The layout is deliberately force-free: counterparties sit
// on a circle ordered by weighted degree, so the same snapshot always draws the same picture
// on every machine and in every screenshot attached to a working paper. No graph library.
type NetNode = {
  id: string
  label: string
  kind: 'subject' | 'counterparty'
  inbound_satang: number
  outbound_satang: number
  volume_satang: number
  tx_count: number
  degree: number
  betweenness: number
  clustering: number
}

type NetEdge = { from: string; to: string; amount_satang: number; tx_count: number }

type NetCycle = {
  nodes: string[]
  labels: string[]
  min_satang: number
  max_satang: number
  spread_bps: number
  tx_count: number
}

type NetMetrics = {
  node_count: number
  counterparty_count: number
  edge_count: number
  transaction_count: number
  unresolved_transaction_count: number
  self_loop_count: number
  total_inbound_satang: number
  total_outbound_satang: number
  density: number
  avg_degree: number
  max_degree: number
  cycle_count: number
}

type NetSnapshot = {
  id: string
  engagement_id: string
  built_at: string
  nodes: NetNode[]
  edges: NetEdge[]
  metrics: NetMetrics
  cycles: NetCycle[]
  top: NetNode[]
}

type NetHistory = {
  id: string
  engagement_id: string
  built_at: string
  node_count: number
  edge_count: number
  cycle_count: number
}

const VIEW_W = 760
const VIEW_H = 560
const CX = VIEW_W / 2
const CY = VIEW_H / 2 - 8
const ORBIT = 200
const SUBJECT_R = 30

const clip = (text: string, max = 22) => (text.length > max ? `${text.slice(0, max - 1)}…` : text)

type Placed = { node: NetNode; x: number; y: number; r: number }

// Circle layout by weighted degree. Ties break on id so the order never depends on the
// browser's sort implementation.
function layout(nodes: NetNode[]): Placed[] {
  const peers = nodes.filter((n) => n.kind !== 'subject')
  const ordered = [...peers].sort(
    (a, b) => b.volume_satang - a.volume_satang || a.id.localeCompare(b.id),
  )
  const maxVolume = Math.max(1, ...ordered.map((n) => n.volume_satang))
  return ordered.map((node, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / Math.max(1, ordered.length)
    return {
      node,
      x: CX + ORBIT * Math.cos(angle),
      y: CY + ORBIT * Math.sin(angle),
      r: 7 + 19 * Math.sqrt(node.volume_satang / maxVolume),
    }
  })
}

// Pull a line back to the circle edge so arrowheads and strokes do not disappear under the
// node fill.
function trimLine(
  x1: number,
  y1: number,
  r1: number,
  x2: number,
  y2: number,
  r2: number,
) {
  const dx = x2 - x1
  const dy = y2 - y1
  const len = Math.hypot(dx, dy) || 1
  const ux = dx / len
  const uy = dy / len
  return {
    x1: x1 + ux * (r1 + 2),
    y1: y1 + uy * (r1 + 2),
    x2: x2 - ux * (r2 + 5),
    y2: y2 - uy * (r2 + 5),
  }
}

export function Network() {
  const queryClient = useQueryClient()
  const [engagementId, setEngagementId] = useState('')

  const engagementsQ = useQuery({
    queryKey: ['engagements'],
    queryFn: () => api<Engagement[]>('/v1/engagements'),
    staleTime: 120_000,
  })
  const engagements = useMemo(() => engagementsQ.data ?? [], [engagementsQ.data])

  // Default to the engagement with the most transactions: it is the one whose network is
  // worth looking at first, and the choice is stable across reloads.
  useEffect(() => {
    if (engagementId || engagements.length === 0) return
    const busiest = [...engagements].sort(
      (a, b) => b.transaction_count - a.transaction_count || a.code.localeCompare(b.code),
    )[0]
    setEngagementId(busiest.id)
  }, [engagementId, engagements])

  const snapshotQ = useQuery({
    queryKey: ['network', engagementId],
    queryFn: () => api<NetSnapshot>(`/v1/forensics/network/${engagementId}`),
    enabled: Boolean(engagementId),
    retry: false,
    staleTime: 60_000,
  })

  const historyQ = useQuery({
    queryKey: ['network-history', engagementId],
    queryFn: () => api<NetHistory[]>(`/v1/forensics/network/${engagementId}/snapshots?limit=5`),
    enabled: Boolean(engagementId),
    staleTime: 60_000,
  })

  const build = useMutation({
    mutationFn: () => apiWrite<NetSnapshot>(`/v1/forensics/network/${engagementId}/build`, 'POST'),
    onSuccess: (snap) => {
      queryClient.setQueryData(['network', engagementId], snap)
      queryClient.invalidateQueries({ queryKey: ['network-history', engagementId] })
    },
  })

  const snap = snapshotQ.data
  const missing = snapshotQ.error instanceof ApiError && snapshotQ.error.status === 404
  const placed = useMemo(() => layout(snap?.nodes ?? []), [snap])
  const placedById = useMemo(() => new Map(placed.map((p) => [p.node.id, p])), [placed])
  const subject = snap?.nodes.find((n) => n.kind === 'subject')

  const maxEdge = Math.max(1, ...(snap?.edges ?? []).map((e) => e.amount_satang))
  const cycleNodes = useMemo(
    () => new Set((snap?.cycles ?? []).flatMap((c) => c.nodes)),
    [snap],
  )
  const cycleEdges = useMemo(() => {
    const set = new Set<string>()
    for (const c of snap?.cycles ?? []) {
      for (let i = 0; i < c.nodes.length; i += 1) {
        set.add(`${c.nodes[i]}|${c.nodes[(i + 1) % c.nodes.length]}`)
      }
    }
    return set
  }, [snap])

  const selected = engagements.find((e) => e.id === engagementId)
  const top = snap?.top?.length ? snap.top : (snap?.nodes.filter((n) => n.kind !== 'subject') ?? [])
  const maxTop = Math.max(1, ...top.map((n) => n.volume_satang))
  const buildError = build.error instanceof ApiError ? build.error.message : build.error ? 'สร้างสแนปช็อตไม่สำเร็จ' : ''

  return (
    <>
      <PageHead
        title="เครือข่ายคู่ค้า"
        sub="กราฟธุรกรรมเชิงนิติวิทยาศาสตร์ · จัดวางตามปริมาณเงิน ไม่มีไลบรารีกราฟภายนอก"
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => build.mutate()}
            disabled={!engagementId || build.isPending}
          >
            {build.isPending ? 'กำลังสร้าง…' : 'สร้างสแนปช็อต'}
          </button>
        }
      />

      <div className="filters">
        <span className="dim" style={{ fontSize: 12 }}>โครงการตรวจสอบ</span>
        <select
          className="select"
          value={engagementId}
          onChange={(e) => setEngagementId(e.target.value)}
          aria-label="เลือกโครงการตรวจสอบ"
        >
          {engagements.length === 0 && <option value="">ยังไม่มีโครงการ</option>}
          {engagements.map((e) => (
            <option key={e.id} value={e.id}>
              {e.code} · {e.title}
            </option>
          ))}
        </select>
        {selected && (
          <span className="dim" style={{ fontSize: 11.5 }}>
            {number(selected.transaction_count)} รายการในบัญชีแยกประเภท
          </span>
        )}
        {snap && (
          <span className="dim" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
            สแนปช็อตเมื่อ {thaiDateTime(snap.built_at)}
          </span>
        )}
      </div>

      {buildError && (
        <div className="card card-pad" style={{ marginBottom: 16, borderColor: 'var(--color-crimson)' }}>
          <span style={{ color: 'var(--color-crimson)', fontSize: 12.5 }}>{buildError}</span>
        </div>
      )}

      {engagementsQ.isLoading || (snapshotQ.isLoading && !missing) ? (
        <div className="stack"><Skel h={120} /><Skel h={360} /></div>
      ) : missing ? (
        <div className="card card-pad">
          <Empty
            title="ยังไม่มีสแนปช็อตของโครงการนี้"
            body="กดสร้างสแนปช็อตเพื่ออ่านธุรกรรมทั้งหมดของโครงการและวาดเครือข่ายคู่ค้า"
          />
        </div>
      ) : !snap ? (
        <div className="card card-pad">
          <Empty title="โหลดเครือข่ายไม่สำเร็จ" body="ลองเลือกโครงการอื่นหรือสร้างสแนปช็อตใหม่" />
        </div>
      ) : (
        <>
          <div className="grid-4 stagger">
            <Stat label="คู่ค้าในเครือข่าย" value={snap.metrics.counterparty_count} unit="ราย"
              foot={`โหนดทั้งหมด ${snap.metrics.node_count} โหนด`} />
            <Stat label="ความสัมพันธ์" value={snap.metrics.edge_count} unit="เส้น"
              foot={`จาก ${number(snap.metrics.transaction_count)} รายการ`} />
            <Stat label="วงจรที่พบ" value={snap.metrics.cycle_count} unit="วงจร"
              foot="เทียบยอดสองทางภายใน 5%" />
            <Stat label="เงินเข้า–ออก" value={money(snap.metrics.total_inbound_satang + snap.metrics.total_outbound_satang, { compact: true })}
              foot={`เข้า ${money(snap.metrics.total_inbound_satang, { compact: true })} · ออก ${money(snap.metrics.total_outbound_satang, { compact: true })}`} />
          </div>

          <div className="grid-main" style={{ marginTop: 18 }}>
            <div className="card card-pad">
              <div className="card-label">แผนที่การไหลของเงิน</div>
              <div style={{ marginTop: 6, overflowX: 'auto' }}>
                <svg
                  viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                  role="img"
                  aria-label="เครือข่ายคู่ค้าของโครงการที่เลือก"
                  style={{ width: '100%', minWidth: 520, height: 'auto', display: 'block' }}
                >
                  <defs>
                    <marker id="net-arrow-out" viewBox="0 0 8 8" refX="7" refY="4"
                      markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                      <path d="M0,0 L8,4 L0,8 z" fill="var(--color-gold)" />
                    </marker>
                    <marker id="net-arrow-in" viewBox="0 0 8 8" refX="7" refY="4"
                      markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                      <path d="M0,0 L8,4 L0,8 z" fill="var(--color-emerald-soft)" />
                    </marker>
                  </defs>

                  <circle cx={CX} cy={CY} r={ORBIT + 44} fill="none"
                    stroke="var(--color-hairline)" strokeDasharray="2 7" />

                  {snap.edges.map((edge) => {
                    const from = edge.from === subject?.id
                      ? { x: CX, y: CY, r: SUBJECT_R }
                      : placedById.get(edge.from)
                    const to = edge.to === subject?.id
                      ? { x: CX, y: CY, r: SUBJECT_R }
                      : placedById.get(edge.to)
                    if (!from || !to) return null
                    const line = trimLine(from.x, from.y, from.r, to.x, to.y, to.r)
                    const inbound = edge.to === subject?.id
                    const inCycle = cycleEdges.has(`${edge.from}|${edge.to}`)
                    const op = 0.14 + 0.62 * (edge.amount_satang / maxEdge)
                    return (
                      <line
                        key={`${edge.from}-${edge.to}`}
                        x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
                        stroke={inCycle ? 'var(--color-crimson)' : inbound ? 'var(--color-emerald-soft)' : 'var(--color-gold)'}
                        strokeWidth={inCycle ? 2 : 1}
                        strokeOpacity={inCycle ? 0.9 : op}
                        markerEnd={inbound ? 'url(#net-arrow-in)' : 'url(#net-arrow-out)'}
                      >
                        <title>{`${edge.tx_count} รายการ · ${money(edge.amount_satang)}`}</title>
                      </line>
                    )
                  })}

                  {placed.map((p, i) => {
                    const hot = cycleNodes.has(p.node.id)
                    const showLabel = i < 8
                    return (
                      <g key={p.node.id}>
                        <circle
                          cx={p.x} cy={p.y} r={p.r}
                          fill={hot ? 'var(--color-crimson)' : 'var(--color-surface-2)'}
                          fillOpacity={hot ? 0.22 : 1}
                          stroke={hot ? 'var(--color-crimson)' : 'var(--color-gold)'}
                          strokeWidth={hot ? 2 : 1}
                          strokeOpacity={hot ? 0.95 : 0.55}
                        >
                          <title>{`${p.node.label}\nรับ ${money(p.node.inbound_satang)} · จ่าย ${money(p.node.outbound_satang)}\n${p.node.tx_count} รายการ`}</title>
                        </circle>
                        {showLabel && (
                          <text
                            x={p.x} y={p.y - p.r - 7}
                            textAnchor="middle"
                            fill="var(--color-ivory-muted)"
                            fontSize={10.5}
                            style={{ pointerEvents: 'none' }}
                          >
                            {clip(p.node.label)}
                          </text>
                        )}
                      </g>
                    )
                  })}

                  {subject && (
                    <g>
                      <circle cx={CX} cy={CY} r={SUBJECT_R}
                        fill="var(--color-gold)" fillOpacity={0.92}
                        stroke="var(--color-gold-bright)" strokeWidth={1.5}>
                        <title>{`${subject.label}\nเข้า ${money(subject.inbound_satang)} · ออก ${money(subject.outbound_satang)}`}</title>
                      </circle>
                      <text x={CX} y={CY + 4} textAnchor="middle" fill="var(--color-void)"
                        fontSize={10} fontWeight={600}>
                        กิจการ
                      </text>
                      <text x={CX} y={CY + SUBJECT_R + 15} textAnchor="middle"
                        fill="var(--color-ivory-muted)" fontSize={10.5}>
                        {clip(subject.label, 34)}
                      </text>
                    </g>
                  )}
                </svg>
              </div>
              <div className="row" style={{ gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                <span className="chip gold">จ่ายออก</span>
                <span className="chip" style={{ color: 'var(--color-emerald-soft)', borderColor: 'var(--color-emerald-soft)' }}>รับเข้า</span>
                <span className="chip" style={{ color: 'var(--color-crimson)', borderColor: 'var(--color-crimson)' }}>อยู่ในวงจร</span>
                <span className="dim" style={{ fontSize: 11 }}>ขนาดวงกลมตามปริมาณเงิน · ความเข้มเส้นตามยอดโอน</span>
              </div>
            </div>

            <div className="stack">
              <div className="card card-pad">
                <div className="card-label">คู่ค้าสำคัญ 10 ราย</div>
                <div className="stack" style={{ marginTop: 14, gap: 12 }}>
                  {top.length === 0 && <span className="dim">ไม่พบคู่ค้าในโครงการนี้</span>}
                  {top.slice(0, 10).map((n, i) => (
                    <div key={n.id}>
                      <div className="row" style={{ gap: 8 }}>
                        <span className="dim num" style={{ width: 16, fontSize: 10.5 }}>{i + 1}</span>
                        <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {n.label}
                        </span>
                        <span className="num" style={{ fontSize: 11.5 }}>{money(n.volume_satang, { compact: true })}</span>
                      </div>
                      <div className="row" style={{ gap: 8, marginTop: 4 }}>
                        <span style={{ width: 16 }} />
                        <span className="bar" style={{ flex: 1 }}>
                          <i style={{ width: `${Math.max(2, (n.volume_satang / maxTop) * 100)}%` }} />
                        </span>
                        <span className="dim" style={{ fontSize: 10 }}>
                          {n.tx_count} รายการ
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card card-pad">
                <div className="card-label">วงจรเงินหมุนเวียน</div>
                {snap.cycles.length === 0 ? (
                  <div style={{ marginTop: 12 }}>
                    <span className="dim" style={{ fontSize: 12 }}>
                      ไม่พบวงจรที่ขาไปและขากลับต่างกันไม่เกิน 5%
                    </span>
                  </div>
                ) : (
                  <div className="stack" style={{ marginTop: 12, gap: 10 }}>
                    {snap.cycles.map((c) => (
                      <div key={c.nodes.join('|')} style={{ borderLeft: '2px solid var(--color-crimson)', paddingLeft: 10 }}>
                        <div style={{ fontSize: 12 }}>
                          {c.labels.join(' → ')} → {c.labels[0]}
                        </div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                          {money(c.min_satang, { compact: true })}–{money(c.max_satang, { compact: true })}
                          {' · '}ต่าง {(c.spread_bps / 100).toFixed(2)}% · {c.tx_count} รายการ
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="card card-pad">
                <div className="card-label">ประวัติสแนปช็อต</div>
                <div className="stack" style={{ marginTop: 12, gap: 8 }}>
                  {(historyQ.data ?? []).length === 0 && (
                    <span className="dim" style={{ fontSize: 11.5 }}>ยังไม่มีประวัติ</span>
                  )}
                  {(historyQ.data ?? []).map((h) => (
                    <div key={h.id} className="row" style={{ gap: 8, fontSize: 11 }}>
                      <span className="dim" style={{ flex: 1 }}>{thaiDateTime(h.built_at)}</span>
                      <span className="num">{h.node_count} โหนด</span>
                      <span className="num">{h.edge_count} เส้น</span>
                      <span className="num" style={{ color: h.cycle_count > 0 ? 'var(--color-crimson)' : 'var(--color-ivory-dim)' }}>
                        {h.cycle_count} วงจร
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 18 }}>
            <div className="card-label">คุณภาพข้อมูลของสแนปช็อต</div>
            <div className="row" style={{ gap: 28, marginTop: 12, flexWrap: 'wrap' }}>
              <span className="dim" style={{ fontSize: 11.5 }}>รายการที่อ่านได้ {number(snap.metrics.transaction_count)}</span>
              <span className="dim" style={{ fontSize: 11.5 }}>รายการที่วางในกราฟไม่ได้ {number(snap.metrics.unresolved_transaction_count)}</span>
              <span className="dim" style={{ fontSize: 11.5 }}>เซลฟ์ลูปที่ถูกตัดออก {number(snap.metrics.self_loop_count)}</span>
              <span className="dim" style={{ fontSize: 11.5 }}>ดีกรีเฉลี่ย {snap.metrics.avg_degree.toFixed(2)}</span>
              <span className="dim" style={{ fontSize: 11.5 }}>ความหนาแน่น {snap.metrics.density.toFixed(4)}</span>
            </div>
          </div>
        </>
      )}
    </>
  )
}
